import React from 'react';

const LobsterAI = () => (
  <article className="news-article-body">
    <p className="news-lead">
      On <strong>February 11, 2026</strong>, NetEase Youdao released
      <strong> LobsterAI (有道龙虾)</strong> — described as "China's first fully open-source
      AI Agent." Under an MIT licence, LobsterAI is a local-first, privacy-preserving
      agentic system that can autonomously handle office productivity, web automation,
      data analysis, and scheduled tasks — all while keeping every byte of user data
      on the local device.
    </p>

    <h2>Why "Lobster" (龙虾)?</h2>
    <p>
      In Chinese internet culture, the lobster (龙虾, <em>lóng xiā</em>, literally "dragon
      shrimp") is associated with tenacity, resilience, and the ability to operate in
      complex environments. NetEase Youdao chose the name to reflect an agent that works
      hard and persistently on behalf of its user — a "24/7 all-scenario personal
      assistant that gets work done for you," as the official description puts it.
    </p>

    <h2>Architecture</h2>
    <p>
      LobsterAI is built on <strong>Electron</strong> with a <strong>React 18</strong> frontend,
      making it cross-platform (Windows, macOS, Linux). It uses the
      <strong>Claude Agent SDK</strong> for its core reasoning and task-execution loop.
      A unique <strong>"Cowork System"</strong> supports three execution modes:
    </p>
    <ul>
      <li><strong>Sandbox</strong>: Isolated environment — safe for untrusted or experimental tasks</li>
      <li><strong>Local</strong>: Direct system access — fastest execution for trusted workflows</li>
      <li><strong>Auto</strong>: Context-dependent — the agent selects the appropriate mode based on risk level</li>
    </ul>

    <h2>16 Built-In Skills</h2>
    <p>LobsterAI ships with 16 pre-built capability modules covering:</p>
    <ul>
      <li><strong>Office productivity</strong>: Excel report generation, PPT creation, document editing</li>
      <li><strong>Web automation</strong>: Browser control, form filling, data scraping</li>
      <li><strong>Media generation</strong>: AI video creation using Remotion</li>
      <li><strong>Communication</strong>: Email management, scheduled newsletters</li>
      <li><strong>Scheduling</strong>: Recurring tasks, timed workflows, news aggregation</li>
      <li><strong>Search</strong>: Web research with structured output</li>
    </ul>

    <h2>Privacy-First Design</h2>
    <p>
      In a landscape where most AI agents are cloud-dependent, LobsterAI makes a deliberate
      architectural choice: <strong>all user data is stored locally in SQLite</strong>.
      No data leaves the device to a third-party server. This positions it directly for
      users in regulated industries or privacy-sensitive contexts where cloud AI agents
      create unacceptable data exposure.
    </p>
    <p>
      Additionally, <strong>all tool actions require explicit user approval</strong> before
      execution — a permission gate that prevents the agent from taking unintended autonomous
      actions, a safety mechanism many agentic systems still lack.
    </p>

    <h2>Mobile Control via Chat Apps</h2>
    <p>
      LobsterAI can be controlled remotely via <strong>DingTalk</strong>, <strong>Feishu</strong>,
      <strong>Telegram</strong>, and <strong>Discord</strong> — bridging the gap between
      desktop automation and mobile control. Users can trigger tasks, check status, and
      receive results through apps already on their phones.
    </p>

    <h2>Persistent Memory</h2>
    <p>
      The agent builds a <strong>persistent memory</strong> of user preferences, recurring
      workflows, and personal context — enabling personalisation that improves over time
      without requiring cloud synchronisation. All memory is stored locally.
    </p>

    <h2>Open-Source Context</h2>
    <p>
      By March 2026, LobsterAI reached version <strong>2026.3.30</strong> with ~4,750 GitHub
      stars. While modest compared to OpenClaw's viral growth, LobsterAI's MIT licence and
      fully open codebase make it significant as a reference implementation for local-first
      agentic AI — particularly for the Chinese developer ecosystem, where distrust of
      US cloud services has accelerated demand for sovereign, locally hosted alternatives.
    </p>

    <div className="news-insight-box">
      <h3>Key Takeaway</h3>
      <p>
        LobsterAI represents a design philosophy — local-first, permission-gated,
        memory-persistent — that addresses the three biggest concerns users have about
        AI agents: privacy, control, and consistency. As agentic AI matures, these
        properties will become baseline requirements rather than differentiators. NetEase
        Youdao has open-sourced the playbook for building trustworthy personal AI agents.
      </p>
    </div>
  </article>
);

export default LobsterAI;
